import axios from 'axios';
import firebase from 'firebase';

// Create custom axios instance
const fullUserAgent = `AmigoWebApp/1.0.0/${navigator.userAgent}`;

const apiClient = axios.create({
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-Custom-User-Agent': fullUserAgent,
  }
});

// Token management utilities
const TokenManager = {
  getApiKey: () => localStorage.getItem('amigo_api_key'),
  setApiKey: (apiKey) => localStorage.setItem('amigo_api_key', apiKey),
  
  getIdToken: () => localStorage.getItem('amigo_id_token'),
  setIdToken: (token) => localStorage.setItem('amigo_id_token', token),
  
  getRefreshToken: () => localStorage.getItem('amigo_refresh_token'),
  setRefreshToken: (token) => localStorage.setItem('amigo_refresh_token', token),
  
  getApiKeyExpiry: () => localStorage.getItem('amigo_api_key_expiry'),
  setApiKeyExpiry: (expiry) => localStorage.setItem('amigo_api_key_expiry', expiry),
  
  clearTokens: () => {
    localStorage.removeItem('amigo_api_key');
    localStorage.removeItem('amigo_id_token');
    localStorage.removeItem('amigo_refresh_token');
    localStorage.removeItem('amigo_api_key_expiry');
  },
  
  isApiKeyExpired: () => {
    const expiry = TokenManager.getApiKeyExpiry();
    if (!expiry) return true;
    return new Date() >= new Date(expiry);
  }
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add API key to headers if available
    const apiKey = TokenManager.getApiKey();
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    }
    
    // Add Authorization Bearer token if available
    const idToken = TokenManager.getIdToken();
    if (idToken) {
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token expiry
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Helper function to perform complete logout
    const performLogout = async () => {
      try {
        // Clear custom authentication tokens
        TokenManager.clearTokens();
        
        // Sign out from Firebase
        await firebase.auth().signOut();
        
        // Clear Vue session by removing vue-session-key from localStorage
        localStorage.removeItem('vue-session-key');
        
        // Redirect to login
        window.location.href = '/login';
      } catch (logoutError) {
        console.error('Error during logout cleanup:', logoutError);
        // Force redirect even if cleanup fails
        window.location.href = '/login';
      }
    };

    // If 401 (Unauthorized), perform complete logout
    if (error.response?.status === 401) {
      await performLogout();
      return Promise.reject(error);
    }
    
    // If 402 with AUTH01 error code (Token expired), perform complete logout
    if (error.response?.status === 402) {
      const errorData = error.response.data;
      
      if (errorData?.error?.error_code === 'AUTH01') {
        await performLogout();
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

export { apiClient, TokenManager };
export default apiClient;
